import type { FilterDefinition } from '@/components/filters/list-filter-types'
import { FilterOptionList } from '@/components/filters/filter-menu'
import type { FeedbackKind } from '@/hooks/use-feedback'
import { FEEDBACK_KINDS } from '@/lib/feedback-display'

export type FeedbackKindFilterValue = FeedbackKind | '' | 'all'

export type FeedbackKindFilterState = {
  search: string
  kind: FeedbackKindFilterValue
}

export type FeedbackKindFilterCtx = {
  counts: Record<string, number>
}

export const FEEDBACK_KIND_OPTIONS: Array<{ value: FeedbackKind | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  ...FEEDBACK_KINDS,
]

export const FEEDBACK_SORT_OPTIONS = [
  { value: 'recent', label: 'Recent' },
  { value: 'votes', label: 'Top votes' },
] as const

export type FeedbackSort = (typeof FEEDBACK_SORT_OPTIONS)[number]['value']

function kindOptionLabel(
  option: { value: FeedbackKind | 'all'; label: string },
  counts: Record<string, number>,
): string {
  if (option.value === 'all') return option.label
  const count = counts[option.value]
  return count > 0 ? `${option.label} (${count})` : option.label
}

function visibleKindOptions(counts: Record<string, number>) {
  return FEEDBACK_KIND_OPTIONS.filter(
    (option) => option.value === 'all' || (counts[option.value] ?? 0) > 0,
  )
}

export function computeFeedbackKindCounts(items: { kind: FeedbackKind }[]): Record<string, number> {
  const counts: Record<string, number> = { all: items.length }
  for (const { value } of FEEDBACK_KINDS) {
    counts[value] = 0
  }
  for (const item of items) {
    counts[item.kind] = (counts[item.kind] ?? 0) + 1
  }
  return counts
}

export const FEEDBACK_KIND_FILTER_DEFINITIONS: FilterDefinition<
  FeedbackKindFilterState,
  FeedbackKindFilterCtx
>[] = [
  {
    key: 'kind',
    label: 'Kind',
    displayValue: (filters, ctx) => {
      const value = filters.kind || 'all'
      const option = FEEDBACK_KIND_OPTIONS.find((opt) => opt.value === value)
      return option ? kindOptionLabel(option, ctx.counts) : 'All'
    },
    isNonDefault: (filters) => Boolean(filters.kind && filters.kind !== 'all'),
    reset: (filters) => ({ ...filters, kind: '' }),
    renderPopover: ({ filters, onChange, ctx }) => (
      <FilterOptionList
        type="single"
        options={visibleKindOptions(ctx.counts).map((option) => ({
          value: option.value,
          label: kindOptionLabel(option, ctx.counts),
        }))}
        value={filters.kind || 'all'}
        onValueChange={(value) =>
          onChange({ ...filters, kind: value === 'all' ? '' : (value as FeedbackKind) })
        }
      />
    ),
  },
]
